import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import Axios from "axios";
import Pagination from "react-js-pagination";
import { confirmAlert } from "react-confirm-alert";
import { URL_LIST_ALL } from "../configs/api";
import { checkRole } from "../model";
import Loading from "../component/Loading";
import SearchBoxModal from "../component/SearchBoxModal";
import ModalXemFile from "./ModalXemFile";

/**
 * Bảng danh sách hợp đồng có chọn nhiều + bộ lọc + xuất file đã merge.
 *
 * Tách riêng khỏi component List.js dùng chung: List.js phục vụ hàng chục màn hình khác
 * nên không sửa để tránh ảnh hưởng dây chuyền.
 */
export default function BangHopDong({ refresh, onSelectChange }) {
    const { user } = useSelector((state) => state.auth);
    const allowEdit = checkRole(user, "hop-dong", "edit");
    const allowDelete = checkRole(user, "hop-dong", "delete");

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [totalRecord, setTotalRecord] = useState(0);
    const [change, setChange] = useState(false);
    const limit = 50;

    // Bộ lọc: `filters` là giá trị đang gõ, `applied` mới là thứ dùng để gọi API.
    const emptyFilter = { f_title: "", f_user_id: "", f_type_id: "" };
    const [filters, setFilters] = useState(emptyFilter);
    const [applied, setApplied] = useState(emptyFilter);

    // Chọn nhiều: lưu cả object để hiển thị/xuất được kể cả khi đã chuyển trang.
    const [selected, setSelected] = useState([]);
    // Hợp đồng đang mở xem file; null = không mở.
    const [viewing, setViewing] = useState(null);

    const isSelected = (id) => selected.some((i) => i.id === id);

    const toggleRow = (row) => {
        setSelected((current) =>
            current.some((i) => i.id === row.id)
                ? current.filter((i) => i.id !== row.id)
                : current.concat(row)
        );
    };

    // Chọn/bỏ chọn toàn bộ các dòng của TRANG hiện tại, không đụng lựa chọn ở trang khác.
    const pageAllSelected = rows.length > 0 && rows.every((r) => isSelected(r.id));
    const toggleAllOnPage = () => {
        setSelected((current) => {
            if (pageAllSelected) {
                const ids = new Set(rows.map((r) => r.id));
                return current.filter((i) => !ids.has(i.id));
            }
            const missing = rows.filter((r) => !current.some((i) => i.id === r.id));
            return current.concat(missing);
        });
    };

    useEffect(() => {
        if (onSelectChange) onSelectChange(selected);
    }, [selected, onSelectChange]);

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        const query = Object.keys(applied)
            .filter((k) => applied[k] !== "" && applied[k] !== null)
            .map((k) => k + "=" + encodeURIComponent(applied[k]))
            .join("&");

        Axios.get(
            URL_LIST_ALL + "hopdong?page=" + page + "&limit=" + limit + (query ? "&" + query : ""),
            user.config
        )
            .then((res) => {
                if (!mounted) return;
                setLoading(false);
                if (res.data.status === "success") {
                    setRows(res.data.data || []);
                    setTotalRecord(res.data.total || 0);
                } else console.log(res.data.message);
            })
            .catch((err) => {
                console.log(err);
                if (mounted) setLoading(false);
            });
        return () => (mounted = false);
    }, [user, page, applied, change, refresh]);

    const applyFilter = useCallback(() => {
        setPage(1);
        setApplied(filters);
    }, [filters]);

    const resetFilter = () => {
        setFilters(emptyFilter);
        setApplied(emptyFilter);
        setPage(1);
    };

    const actionDelete = (id) => {
        confirmAlert({
            title: "Thông báo",
            message: "Bạn có chắc muốn xoá hợp đồng này?",
            buttons: [
                {
                    label: "Có",
                    onClick: () => {
                        Axios.delete(URL_LIST_ALL + "hopdong/" + id, user.config)
                            .then((res) => {
                                if (res.data.status === "success") {
                                    setSelected((current) => current.filter((i) => i.id !== id));
                                    setChange((c) => !c);
                                } else alert(res.data.message);
                            })
                            .catch((err) => console.log(err));
                    },
                },
                { label: "Không" },
            ],
        });
    };

    return (
        <>
            {/* Bộ lọc */}
            <div className="row mb-2">
                <div className="col-12 col-sm-4 form-group">
                    <label>Tiêu đề</label>
                    <input
                        type="text"
                        className="form-control height32"
                        placeholder="Tìm theo tiêu đề"
                        value={filters.f_title}
                        onChange={(e) => setFilters({ ...filters, f_title: e.target.value })}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                applyFilter();
                            }
                        }}
                        autoComplete="off"
                    />
                </div>
                <div className="col-12 col-sm-3 form-group">
                    <label>Đối tượng áp dụng</label>
                    <SearchBoxModal
                        apiname={"users"}
                        placeholder="Chọn nhân sự"
                        colvalue={"id"}
                        colname={"fullname"}
                        defaultValue={filters.f_user_id}
                        name="f_user_id"
                        onChange={(e) => setFilters({ ...filters, f_user_id: e || "" })}
                    />
                </div>
                <div className="col-12 col-sm-3 form-group">
                    <label>Loại hợp đồng</label>
                    <SearchBoxModal
                        apiname={"hopdong_type"}
                        placeholder="Chọn mẫu hợp đồng"
                        colvalue={"id"}
                        colname={"title"}
                        defaultValue={filters.f_type_id}
                        name="f_type_id"
                        onChange={(e) => setFilters({ ...filters, f_type_id: e || "" })}
                    />
                </div>
                <div className="col-12 col-sm-2 form-group d-flex align-items-end">
                    <button type="button" className="btn btn-sm btn-success mr-2 height32" onClick={applyFilter}>
                        <span className="fas fa-search"></span> Lọc
                    </button>
                    <button type="button" className="btn btn-sm btn-secondary height32" onClick={resetFilter}>
                        Bỏ lọc
                    </button>
                </div>
            </div>

            <div className="w-100 d-flex justify-content-between align-items-center mb-2">
                <div>{totalRecord ? "Tìm được tổng: " + totalRecord + " dữ liệu" : ""}</div>
                {loading && <Loading />}
            </div>

            <table className="table table-striped table-bordered table-hover">
                <thead>
                    <tr>
                        <th style={{ width: 40 }}>
                            <input
                                type="checkbox"
                                checked={pageAllSelected}
                                onChange={toggleAllOnPage}
                                title="Chọn tất cả trong trang"
                            />
                        </th>
                        <th style={{ width: 70 }}>ID</th>
                        <th>Tiêu đề</th>
                        <th>Đối tượng áp dụng</th>
                        <th>Loại hợp đồng</th>
                        <th>Người tạo</th>
                        <th style={{ width: 130 }}>File</th>
                        <th style={{ width: 120 }}>Thao tác</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <tr key={row.id} className={isSelected(row.id) ? "table-info" : ""}>
                            <td>
                                <input type="checkbox" checked={isSelected(row.id)} onChange={() => toggleRow(row)} />
                            </td>
                            <td>{row.id}</td>
                            <td>{row.title}</td>
                            <td>
                                {row.fullname}
                                {row.user_code ? " (" + row.user_code + ")" : ""}
                            </td>
                            <td>{row.type_name}</td>
                            <td>{row.author_name}</td>
                            <td>
                                {row.file_url ? (
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() => setViewing(row)}
                                        title="Xem nội dung file đã merge"
                                    >
                                        <span className="fas fa-eye"></span> Xem file
                                    </button>
                                ) : (
                                    <span className="text-muted">Chưa có</span>
                                )}
                            </td>
                            <td>
                                {allowEdit && (
                                    <Link to={"/edit-hop-dong/" + row.id} className="btn btn-sm btn-info mr-1">
                                        <span className="fas fa-edit"></span>
                                    </Link>
                                )}
                                {allowDelete && (
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-danger"
                                        onClick={() => actionDelete(row.id)}
                                    >
                                        <span className="fas fa-trash"></span>
                                    </button>
                                )}
                            </td>
                        </tr>
                    ))}
                    {rows.length === 0 && !loading && (
                        <tr>
                            <td colSpan={8} className="text-center text-muted">
                                Không có dữ liệu
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>

            {totalRecord > limit && (
                <div className="w-100 d-flex justify-content-center mt-2 pagination-group">
                    <Pagination
                        activePage={page}
                        itemsCountPerPage={limit}
                        totalItemsCount={totalRecord}
                        pageRangeDisplayed={5}
                        itemClass="page-item"
                        linkClass="page-link"
                        prevPageText="<"
                        nextPageText=">"
                        onChange={setPage}
                    />
                </div>
            )}

            <ModalXemFile
                show={!!viewing}
                onHide={() => setViewing(null)}
                url={viewing ? viewing.file_url : ""}
                title={viewing ? viewing.title : ""}
            />
        </>
    );
}
